// Iter 133e — AI-assisted specialty proposal review modal.
// Opens from the audit modal when the PM clicks "AI suggest specialties".
// Shows GPT-5.2's per-practitioner proposal with a confidence pill and an
// editable dropdown — PM can override, deselect rows, then apply.
import { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Loader2, Sparkles, CheckCircle2, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONFIDENCE_STYLES = {
  high:   { bg: 'bg-green-100', fg: 'text-green-800', label: 'high' },
  medium: { bg: 'bg-amber-100', fg: 'text-amber-800', label: 'medium' },
  low:    { bg: 'bg-red-100',   fg: 'text-red-800',   label: 'low' },
};

export default function AISpecialtyProposeModal({ onClose, onApplied }) {
  const [loading, setLoading] = useState(true);
  const [proposals, setProposals] = useState([]);
  const [selected, setSelected] = useState({});   // {i: bool} — checked rows
  const [overrides, setOverrides] = useState({}); // {i: codeOverride}
  const [validCodes, setValidCodes] = useState([]);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');
  const [categories, setCategories] = useState('Specialist,Other');
  const [maxClinics, setMaxClinics] = useState(50);

  useEffect(() => {
    axios.get(`${API}/referrals/kestral-specialties`, { withCredentials: true })
      .then(r => setValidCodes(r.data));
  }, []);

  const fetchProposals = async () => {
    setLoading(true); setErr(''); setResult(null);
    try {
      const r = await axios.post(`${API}/referrals/ai-specialty/propose`, {
        categories: categories.split(',').map(s => s.trim()).filter(Boolean),
        only_default_specialty: true,
        only_default_value: 'GeneralMedicine',
        max_clinics: maxClinics,
      }, { withCredentials: true });
      const ps = r.data.proposals || [];
      setProposals(ps);
      // Pre-select rows with high confidence
      const sel = {}; const ovr = {};
      ps.forEach(p => {
        sel[p.i] = p.confidence === 'high';
        ovr[p.i] = p.proposed_specialty;
      });
      setSelected(sel); setOverrides(ovr);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'AI proposal failed');
    }
    setLoading(false);
  };
  useEffect(() => { fetchProposals(); /* fetchProposals intentionally not in deps */ }, []);

  const toggleAll = (val) => {
    const sel = {}; proposals.forEach(p => { sel[p.i] = val; }); setSelected(sel);
  };
  const selectedCount = Object.values(selected).filter(Boolean).length;

  const apply = async () => {
    const items = proposals.filter(p => selected[p.i]).map(p => ({
      referrer_id: p.referrer_id,
      practitioner_index: p.practitioner_index,
      specialty: overrides[p.i] || p.proposed_specialty,
    }));
    if (items.length === 0) return;
    if (!window.confirm(`Apply ${items.length} AI-proposed specialty value(s)?`)) return;
    setApplying(true);
    try {
      const r = await axios.post(`${API}/referrals/ai-specialty/apply`, { items },
        { withCredentials: true });
      setResult(r.data);
      onApplied?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Apply failed');
    }
    setApplying(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
           data-testid="ai-specialty-modal"
           className="bg-card rounded-xl border max-w-4xl w-full shadow-2xl max-h-[92vh] flex flex-col">
        <div className="px-5 py-3.5 border-b flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold" style={{ color: '#0d2644' }}>AI-suggest specialties</h2>
              <div className="text-xs text-muted-foreground">GPT-5.2 reads clinic + practitioner names and proposes a Kestral specialty per row.</div>
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-muted" data-testid="ai-specialty-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-3 border-b flex items-end gap-3 bg-muted/20 flex-wrap">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground block mb-0.5">Categories</label>
            <input value={categories} onChange={e => setCategories(e.target.value)}
              data-testid="ai-specialty-categories"
              className="text-sm border rounded-lg px-2.5 py-1 w-56" placeholder="Specialist,Other" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground block mb-0.5">Max clinics</label>
            <input type="number" value={maxClinics} onChange={e => setMaxClinics(Number(e.target.value)||50)}
              data-testid="ai-specialty-max"
              className="text-sm border rounded-lg px-2.5 py-1 w-20" />
          </div>
          <button onClick={fetchProposals} disabled={loading}
            data-testid="ai-specialty-reload"
            className="text-sm font-semibold px-3 py-1.5 rounded-lg bg-purple-700 text-white inline-flex items-center gap-1.5 disabled:opacity-50">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            Re-propose with GPT-5.2
          </button>
          <div className="ml-auto text-xs text-muted-foreground">
            {proposals.length} proposal{proposals.length===1?'':'s'} · {selectedCount} selected
          </div>
        </div>

        <div className="overflow-y-auto flex-1 px-5 py-3">
          {loading ? (
            <div className="py-12 flex flex-col items-center text-muted-foreground text-sm">
              <Loader2 className="w-6 h-6 animate-spin mb-2" />
              GPT-5.2 is reading your clinic data…
            </div>
          ) : err ? (
            <div className="rounded-lg p-3 bg-red-50 border border-red-200 text-sm text-red-700">{err}</div>
          ) : result ? (
            <div className="rounded-lg p-4 bg-green-50 border border-green-200" data-testid="ai-specialty-result">
              <div className="font-bold text-green-800 inline-flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4" /> Updated {result.practitioners_updated} practitioner(s).
              </div>
              {result.skipped?.length > 0 && (
                <div className="text-xs text-amber-800 mt-2">
                  {result.skipped.length} row(s) skipped (invalid code or missing index).
                </div>
              )}
            </div>
          ) : proposals.length === 0 ? (
            <div className="rounded-lg p-4 bg-amber-50 border border-amber-200 text-sm text-amber-800 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5" />
              <div>
                Nothing matched those filters. Check the categories are correct, or remove
                the &quot;only fill GeneralMedicine defaults&quot; filter.
              </div>
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="ai-specialty-table">
              <thead>
                <tr className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground border-b">
                  <th className="px-1 py-1.5 text-left w-8">
                    <input type="checkbox" checked={proposals.every(p => selected[p.i])}
                      onChange={e => toggleAll(e.target.checked)} data-testid="ai-specialty-toggle-all" />
                  </th>
                  <th className="px-1 py-1.5 text-left">Clinic</th>
                  <th className="px-1 py-1.5 text-left">Practitioner</th>
                  <th className="px-1 py-1.5 text-left">Proposed</th>
                  <th className="px-1 py-1.5 text-left">Confidence</th>
                  <th className="px-1 py-1.5 text-left">Reason</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {proposals.map(p => {
                  const cs = CONFIDENCE_STYLES[p.confidence] || CONFIDENCE_STYLES.low;
                  return (
                    <tr key={p.i} className={selected[p.i] ? '' : 'opacity-50'} data-testid={`ai-specialty-row-${p.i}`}>
                      <td className="px-1 py-2">
                        <input type="checkbox" checked={!!selected[p.i]}
                          onChange={e => setSelected({ ...selected, [p.i]: e.target.checked })}
                          data-testid={`ai-specialty-check-${p.i}`} />
                      </td>
                      <td className="px-1 py-2 text-xs">{p.clinic_name}</td>
                      <td className="px-1 py-2 text-xs">{p.practitioner_name}</td>
                      <td className="px-1 py-2">
                        <select value={overrides[p.i] || p.proposed_specialty}
                          onChange={e => setOverrides({ ...overrides, [p.i]: e.target.value })}
                          className="text-xs border rounded px-1.5 py-0.5 max-w-[180px]"
                          data-testid={`ai-specialty-select-${p.i}`}>
                          {validCodes.map(c => <option key={c.code} value={c.code}>{c.code}</option>)}
                        </select>
                      </td>
                      <td className="px-1 py-2">
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${cs.bg} ${cs.fg}`}>
                          {cs.label}
                        </span>
                      </td>
                      <td className="px-1 py-2 text-[11px] text-muted-foreground">{p.reason}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="px-5 py-3 border-t flex items-center justify-between bg-muted/20">
          <div className="text-[11px] text-muted-foreground">
            Pre-selects high-confidence rows. Override the specialty in any row before applying.
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="text-sm font-semibold px-4 py-1.5 rounded-lg border hover:bg-muted/40"
              data-testid="ai-specialty-cancel">
              Close
            </button>
            <button onClick={apply} disabled={applying || selectedCount === 0}
              data-testid="ai-specialty-apply"
              className="text-sm font-semibold px-4 py-1.5 rounded-lg bg-[#0d2644] text-white inline-flex items-center gap-1.5 disabled:opacity-50">
              {applying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
              Apply {selectedCount} selected
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
